// Currency APPEARANCE (glyph vs ISO code, the canonical Intl options) is decided in exactly ONE place —
// `@ibada/shared-types/finance` — so a label can never disagree with an amount. Imported for local use and
// re-exported only so existing tenant importers keep their `@/shared/lib/formatting/currency` path; the definition lives in
// shared-types, not here.
import { currencyDisplayFor, currencyFormatOptions, currencySymbol } from "@ibada/shared-types/finance"

export { currencyDisplayFor, currencyFormatOptions, currencySymbol }

/** App-wide default when a caller has no resolved user locale to pass. */
export const DEFAULT_CURRENCY_LOCALE = "en-US"

/**
 * A money amount to display.
 *
 * A `string` is the canonical wire form and the one to prefer: `Intl` parses a
 * decimal string EXACTLY, whereas a `number` has already been through IEEE-754
 * and may have lost a cent before it ever reached this function. `number` stays
 * accepted while surfaces migrate.
 */
export type DisplayAmount = number | string

/**
 * A memoizable currency formatter.
 *
 * It deliberately accepts a {@link DisplayAmount} rather than the `number` that `Intl.NumberFormat.format`
 * advertises: TS's lib types did not learn about string input until ES2023, and the stale signature is what
 * pushed every caller to write `Number(amount)` first — throwing away the exact decimal the API had just gone
 * to the trouble of sending. Narrowing the type here would be narrowing reality.
 */
export interface CurrencyFormatter {
  format(amount: DisplayAmount): string
}

/**
 * Builds a currency formatter. Prefer this when you format many values (memoize the instance) over calling
 * {@link formatCurrency} in a loop.
 */
export function createCurrencyFormatter(
  currency: string,
  locale: string = DEFAULT_CURRENCY_LOCALE,
): CurrencyFormatter {
  const formatter = new Intl.NumberFormat(locale, { ...currencyFormatOptions(currency), currency })
  // `Intl.NumberFormat.format` accepts a decimal string and parses it EXACTLY — this single cast, here, is what
  // buys every call site the right to hand it the canonical string untouched.
  return { format: (amount) => formatter.format(amount as number) }
}

/** Formats a single amount as currency using the canonical options. */
export function formatCurrency(
  amount: DisplayAmount,
  currency: string,
  locale: string = DEFAULT_CURRENCY_LOCALE,
) {
  return createCurrencyFormatter(currency, locale).format(amount)
}

const KPI_COMPACT_THRESHOLD = 1_000_000

/**
 * Format a currency amount for compact KPI display. Below one million
 * we render the canonical full amount (`$999,999.00`); at or above
 * one million we switch to Intl's locale-aware compact notation
 * (`$1.2M`, `1,2 M $`, `$2.5B`).
 *
 * Keeps KPI cards from overflowing on parishes that handle big
 * numbers, while preserving precision for everyday amounts.
 */
export function formatKpiCurrency(
  amount: DisplayAmount,
  currency: string,
  locale: string = DEFAULT_CURRENCY_LOCALE,
) {
  // The threshold is a layout decision, not an accounting one, so reading the magnitude as a float is safe here.
  // The VALUE handed to `format` is always the original, so nothing displayed is ever rounded by this comparison.
  if (Math.abs(Number(amount)) < KPI_COMPACT_THRESHOLD) {
    return createCurrencyFormatter(currency, locale).format(amount)
  }
  return new Intl.NumberFormat(locale, {
    ...currencyFormatOptions(currency),
    currency,
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(amount as number)
}
